import { useCallback, useEffect, useMemo, useState } from 'react';
import { toDatetimeLocal } from '../../../utils/datetime';

// Per-route shift-time edit state. Initial values seeded from the
// BE-provided `baseVehicle`; re-seeded when the vehicle's id changes
// OR when any of the three time fields shifts in-place (a poll-driven
// baseline refresh). `serialize()` returns the override shape the Update
// Route reroute merges into its vehicles payload (see
// RouteOperationsTab.handleRouteUpdateConfirmed).

// Null when the three shift times form a valid window, else a message.
// Values may be BE-shaped ("YYYY-MM-DD HH:MM:SS") or datetime-local
// ("YYYY-MM-DDTHH:MM") depending on whether the field was edited;
// toDatetimeLocal normalizes both so lexicographic comparison is
// chronological.
export function validateShiftTimes({ shiftStart, targetShiftEnd, shiftEnd }) {
  const start = toDatetimeLocal(shiftStart);
  const target = toDatetimeLocal(targetShiftEnd);
  const end = toDatetimeLocal(shiftEnd);
  if (!start || !target || !end) {
    return 'All shift times are required.';
  }
  if (end <= start) {
    return 'Shift end must be after shift start.';
  }
  if (target <= start || target > end) {
    return 'Target end must fall between shift start and shift end.';
  }
  return null;
}

export default function useRouteVehicleEdits(routeName, baseVehicle) {
  const baseStart = baseVehicle?.shiftStartTime || '';
  const baseEnd = baseVehicle?.shiftEndTime || '';
  const baseTargetEnd = baseVehicle?.targetShiftEndTime || '';

  const [shiftStart, setShiftStart] = useState(baseStart);
  const [shiftEnd, setShiftEnd] = useState(baseEnd);
  const [targetShiftEnd, setTargetShiftEnd] = useState(baseTargetEnd);

  useEffect(() => {
    setShiftStart(baseStart);
    setShiftEnd(baseEnd);
    setTargetShiftEnd(baseTargetEnd);
  }, [baseVehicle?.id, baseStart, baseEnd, baseTargetEnd]);

  const isDirty = useMemo(
    () =>
      shiftStart !== baseStart ||
      shiftEnd !== baseEnd ||
      targetShiftEnd !== baseTargetEnd,
    [shiftStart, shiftEnd, targetShiftEnd, baseStart, baseEnd, baseTargetEnd],
  );

  // Only report problems the supervisor introduced — a pristine (possibly
  // odd-looking) BE baseline stays quiet until a field is edited.
  const validationError = useMemo(
    () =>
      isDirty
        ? validateShiftTimes({ shiftStart, targetShiftEnd, shiftEnd })
        : null,
    [isDirty, shiftStart, targetShiftEnd, shiftEnd],
  );

  const reset = useCallback(() => {
    setShiftStart(baseStart);
    setShiftEnd(baseEnd);
    setTargetShiftEnd(baseTargetEnd);
  }, [baseStart, baseEnd, baseTargetEnd]);

  const serialize = useCallback(
    () => ({
      routeName,
      vehicleId: baseVehicle?.id || '',
      shiftStartTime: shiftStart,
      shiftEndTime: shiftEnd,
      targetShiftEndTime: targetShiftEnd,
    }),
    [routeName, baseVehicle, shiftStart, shiftEnd, targetShiftEnd],
  );

  return {
    shiftStart,
    shiftEnd,
    targetShiftEnd,
    setShiftStart,
    setShiftEnd,
    setTargetShiftEnd,
    isDirty,
    validationError,
    reset,
    serialize,
  };
}
